/**
 * The Review step's ask (TMMAPP-0123 item 18): the import left the account's opening pending (the
 * file suggested none, or the person left it blank), so it asks "What was your balance at the end of
 * {date}?" with **Save** (PATCH `openingBalanceMinor` / `openingBalanceOn`) and **Skip for now** (the
 * account stays pending; the card's "Set starting balance" stays).
 *
 * The date is editable (TMMAPP-0125 item 1), prefilled with the day before this file's first line.
 * An account with earlier lines refuses it (`409 OPENING_AFTER_FIRST_LINE`): the date moves to the
 * day before `earliestPostedOn`, the amount stays, and focus returns to the amount. The re-check
 * sentence is a prompt (helper text), not a field error. Save stays available.
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountsApi } from '@/api/accounts';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError, openingAfterFirstLine } from '@/api/errors';
import { SubmitButton } from '@/components/SubmitButton';
import { addCivilDays } from '@/shared/servedWindow';
import { applyBackendFields } from '@/features/shared/useFieldErrors';
import { formatCivilDate, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';

const CIVIL = /^\d{4}-\d{2}-\d{2}$/;
const SERVER_FIELDS = ['openingBalanceMinor', 'openingBalanceOn'] as const;

type Errors = { amount?: string | undefined; on?: string | undefined; form?: string | undefined };

interface OpeningAskProps {
  accountId: number;
  /** The account's own currency. */
  currency: string | null;
  /** The day before the first imported line: the date's prefill. */
  on: string;
  /** Saved: the host re-reads the account. */
  onSaved: () => void;
}

export function OpeningAsk({ accountId, currency, on: initialOn, onSaved }: OpeningAskProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const [amount, setAmount] = useState('');
  const [on, setOn] = useState(initialOn);
  const [errors, setErrors] = useState<Errors>({});
  // The date moved. The amount is still valid; the sentence asks them to check it.
  const [recheck, setRecheck] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<'asking' | 'saved' | 'skipped'>('asking');
  const amountRef = useRef<HTMLInputElement>(null);
  const onRef = useRef<HTMLInputElement>(null);
  const formErrorRef = useRef<HTMLDivElement>(null);
  // A refusal takes focus to its field (or the form's error) once the form renders enabled again.
  const focusNext = useRef(false);
  const question = t('imports.opening.ask', {
    date: formatCivilDate(CIVIL.test(on) ? on : initialOn, locale),
  });

  useEffect(() => {
    if (!focusNext.current || busy) return;
    focusNext.current = false;
    if (errors.amount !== undefined || recheck !== null) amountRef.current?.focus();
    else if (errors.on !== undefined) onRef.current?.focus();
    else if (errors.form !== undefined) formErrorRef.current?.focus();
  }, [errors, busy, recheck]);

  if (state === 'skipped') return null;
  if (state === 'saved') {
    return (
      <Alert severity="success" data-opening-ask="saved">
        {t('imports.opening.saved')}
      </Alert>
    );
  }

  function refuse(next: Errors) {
    focusNext.current = true;
    setErrors(next);
  }

  async function handleSave() {
    if (busy) return;
    const next: Errors = {};
    const amountMinor = parseMoneyToMinor(amount, currency, locale);
    if (amountMinor === null) next.amount = t('errors.fieldInvalid');
    if (!CIVIL.test(on)) next.on = t('errors.fieldInvalid');
    else if (on > today) next.on = t('errors.server.notFuture');
    if (amountMinor === null || next.on !== undefined) {
      refuse(next);
      return;
    }
    setBusy(true);
    setErrors({});
    setRecheck(null);
    try {
      await accountsApi.update(accountId, {
        openingBalanceMinor: amountMinor,
        openingBalanceOn: on,
      });
      setState('saved');
      onSaved();
    } catch (err) {
      const afterFirstLine = openingAfterFirstLine(err);
      if (afterFirstLine !== null) {
        const earliest = afterFirstLine.earliestPostedOn;
        if (earliest !== null) {
          const movedTo = addCivilDays(earliest, -1);
          setOn(movedTo);
          // The amount was typed for the old date. Keep it, and ask them to check it for the new one.
          focusNext.current = true;
          setRecheck(
            t('imports.opening.dateMoved', {
              date: formatCivilDate(movedTo, locale),
            }),
          );
          setErrors({});
        } else {
          setRecheck(null);
          refuse({ on: t('errors.fieldInvalid') });
        }
      } else if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
        const placed: Errors = {};
        const result = applyBackendFields(
          err,
          SERVER_FIELDS,
          (field, message) => {
            if (field === 'openingBalanceMinor') placed.amount = message;
            else placed.on = message;
          },
          t,
        );
        // Nothing of the ask's named (another field, or no fields bag): no fields to highlight.
        refuse(result.placed === 0 ? { form: t('accounts.errorGeneric') } : placed);
      } else {
        refuse({ form: errorCopy(t, err, 'accounts.errorGeneric') });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      component="form"
      data-opening-ask=""
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave();
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography variant="h4" component="h3">
        {t('accounts.openingTitle')}
      </Typography>
      {errors.form !== undefined && (
        <Alert severity="error" ref={formErrorRef} tabIndex={-1}>
          {errors.form}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5, maxWidth: 640 }}>
        <TextField
          label={question}
          value={amount}
          inputRef={amountRef}
          onChange={(event) => {
            setAmount(event.target.value);
            setRecheck(null);
            setErrors((prev) => ({ ...prev, amount: undefined }));
          }}
          disabled={busy}
          error={errors.amount !== undefined}
          helperText={errors.amount ?? recheck ?? t('imports.opening.amountHelp')}
          fullWidth
        />
        <TextField
          type="date"
          label={t('accounts.openingOnLabel')}
          value={on}
          inputRef={onRef}
          onChange={(event) => {
            setOn(event.target.value);
            setRecheck(null);
            setErrors((prev) => ({ ...prev, on: undefined }));
          }}
          disabled={busy}
          error={errors.on !== undefined}
          helperText={errors.on ?? t('accounts.openingHelpEdit')}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
          fullWidth
        />
      </Stack>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <SubmitButton type="submit" loading={busy}>
          {t('common.save')}
        </SubmitButton>
        <Button disabled={busy} onClick={() => setState('skipped')}>
          {t('imports.opening.skip')}
        </Button>
      </Stack>
    </Box>
  );
}
